// Integration data model and vendor logo rendering.
// Logos render as colored vendor-style marks. Where a vendor's official SVG is
// not bundled, a colored monogram is used (still in vendor brand color). Real
// SVGs from /public/logos/integrations/<slug>.svg are used when present at
// runtime via an <img> fallback chain.

import React from 'react';
import {
  IMPORT_CAPABILITY_IDS,
  INTEGRATION_CATALOG,
  LIMITED_SETUP_NAMES,
  ROADMAP_NAMES,
  REGIONAL_LOGO_CLUSTERS,
  getCatalogIntegration,
} from './integration-catalog.js';

export {
  IMPORT_CAPABILITY_IDS,
  INTEGRATION_CATALOG as INTEGRATIONS,
  INTEGRATION_CATALOG as PUBLIC_INTEGRATIONS,
  LIMITED_SETUP_NAMES,
  ROADMAP_NAMES,
  REGIONAL_LOGO_CLUSTERS,
};

export function getIntegration(name) {
  return getCatalogIntegration(name);
}

// Inline brand marks for vendors whose SVGs are already shipped in this repo
// (originally lived in HeroAndReport.jsx). For any other vendor, the component
// looks for a licensed SVG at /logos/integrations/<slug>.svg and falls back to
// a colored monogram if that asset isn't present yet. To add a real logo for a
// new vendor, drop the SVG file in public/logos/integrations/<slug>.svg using
// the slug from INTEGRATIONS above.
export const INLINE_VENDOR_LOGOS = {
  jira: (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <defs>
        <linearGradient id="sw-jira-a" x1="22.034" y1="11.276" x2="16.512" y2="16.978" gradientUnits="userSpaceOnUse">
          <stop offset="0.176" stopColor="#0052CC"/>
          <stop offset="1" stopColor="#2684FF"/>
        </linearGradient>
        <linearGradient id="sw-jira-b" x1="9.97" y1="20.71" x2="15.485" y2="15.034" gradientUnits="userSpaceOnUse">
          <stop offset="0.176" stopColor="#0052CC"/>
          <stop offset="1" stopColor="#2684FF"/>
        </linearGradient>
      </defs>
      <path fill="#2684FF" d="M30.305 15.065 17.038 1.798 15.756.516 5.79 10.483l-4.567 4.582a1.328 1.328 0 0 0 0 1.876l9.119 9.119 5.414 5.414 9.967-9.968.153-.152 4.43-4.413a1.328 1.328 0 0 0 0-1.876zm-14.549 5.413-4.567-4.554 4.567-4.567 4.567 4.567z"/>
      <path fill="url(#sw-jira-a)" d="M15.756 11.358a7.672 7.672 0 0 1-.033-10.815L5.781 10.486l5.408 5.408z"/>
      <path fill="url(#sw-jira-b)" d="m20.335 15.924-4.579 4.58a7.672 7.672 0 0 1 0 10.849l9.995-9.994z"/>
    </svg>
  ),
  github: (
    // Dark variant for light backgrounds. Replace with a light fill via CSS
    // when rendered on dark surfaces.
    <svg viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#181717" d="M12 .297c-6.63 0-12 5.373-12 12 0 5.303 3.438 9.8 8.205 11.385.6.111.82-.261.82-.577 0-.285-.01-1.04-.015-2.04-3.338.724-4.042-1.61-4.042-1.61C4.422 18.07 3.633 17.7 3.633 17.7c-1.087-.744.084-.729.084-.729 1.205.084 1.838 1.236 1.838 1.236 1.07 1.835 2.807 1.305 3.492.998.108-.776.417-1.305.76-1.605-2.665-.3-5.466-1.332-5.466-5.93 0-1.31.465-2.38 1.235-3.22-.135-.303-.54-1.523.105-3.176 0 0 1.005-.322 3.3 1.23.96-.267 1.98-.399 3-.405 1.02.006 2.04.138 3 .405 2.28-1.552 3.285-1.23 3.285-1.23.645 1.653.24 2.873.12 3.176.765.84 1.23 1.91 1.23 3.22 0 4.61-2.805 5.625-5.475 5.92.42.36.81 1.096.81 2.22 0 1.606-.015 2.896-.015 3.286 0 .315.21.69.825.57C20.565 22.092 24 17.592 24 12.297c0-6.627-5.373-12-12-12"/>
    </svg>
  ),
  gitlab: (
    <svg viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#E24329" d="m31.462 12.78-.044-.116L27.071 1.302a1.135 1.135 0 0 0-2.165.08l-2.937 8.992H10.027L7.09 1.382a1.135 1.135 0 0 0-2.165-.08L.586 12.667.542 12.78a8.073 8.073 0 0 0 2.679 9.322l.015.012.04.029 6.601 4.945 3.265 2.468 1.989 1.504a1.336 1.336 0 0 0 1.612 0l1.99-1.504 3.264-2.468 6.64-4.973.018-.013a8.073 8.073 0 0 0 2.678-9.322z"/>
      <path fill="#FC6D26" d="m31.462 12.78-.044-.116a14.671 14.671 0 0 0-5.847 2.63L16.004 22.33c3.198 2.418 5.986 4.522 5.986 4.522l6.64-4.972.018-.014a8.073 8.073 0 0 0 2.679-9.322z"/>
      <path fill="#FCA326" d="m10.018 26.852 3.265 2.468 1.989 1.504a1.336 1.336 0 0 0 1.612 0l1.99-1.504 3.264-2.468s-2.792-2.111-5.99-4.522a1934.81 1934.81 0 0 0-5.99 4.522z"/>
      <path fill="#FC6D26" d="M6.43 15.294A14.671 14.671 0 0 0 .582 12.664L.542 12.78a8.073 8.073 0 0 0 2.679 9.322l.015.012.04.029 6.601 4.945s2.792-2.107 5.99-4.522z"/>
    </svg>
  ),
  linear: (
    <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path fill="#5E6AD2" d="M1.225 61.523c-.388-1.452.957-2.797 2.41-2.409l36.847 9.86c1.452.39 1.913 2.115.855 3.173l-26.93 26.93c-1.057 1.058-2.783.597-3.172-.855zM1.226 46.087c-.025.395.166.78.45 1.062l52.066 52.066c.283.282.667.475 1.061.448a50.04 50.04 0 0 0 4.16-.484c1.218-.196 1.66-1.7.787-2.575L4.31 41.165c-.875-.875-2.38-.431-2.575.787a50.043 50.043 0 0 0-.508 4.135zm6.305-15.74c-.183.39-.101.852.207 1.16L68.493 92.26c.31.309.81.39 1.16.207a50.044 50.044 0 0 0 7.18-4.66c.484-.378.527-1.098.092-1.532L13.722 23.07c-.434-.434-1.154-.39-1.531.092a50.043 50.043 0 0 0-4.66 7.184zm22.84-22.86C39.66 1.61 44.665 0 50 0c27.614 0 50 22.386 50 50 0 5.335-1.611 10.34-3.487 14.628a1.2 1.2 0 0 1-1.948.346L7.026 5.435a1.2 1.2 0 0 1 .346-1.948z"/>
    </svg>
  ),
};

// IntegrationLogo renders a vendor mark using the best available source:
//   1. Inline SVG from INLINE_VENDOR_LOGOS (committed in this repo)
//   2. Licensed SVG file at /logos/integrations/<file || slug.svg> (drop yours here)
//   3. Colored monogram fallback so layouts never break
//
// Real logos are rendered "bare" (no colored disc behind them) so brand colors
// come from the artwork itself. The monogram fallback uses the vendor color as
// a tinted background.
//
// `onDark` marks a dark-surface context (page heroes): vendors with a
// `fileDark` variant swap to it, and the `--on-dark` class lets CSS re-ink
// inline marks whose brand color is too dark to read there (GitHub).
export function IntegrationLogo({ name, size = 28, className = '', onDark = false }) {
  const integ = getIntegration(name);
  if (!integ) return null;
  const inline = INLINE_VENDOR_LOGOS[integ.slug];
  const [imgLoaded, setImgLoaded] = React.useState(false);
  const [imgFailed, setImgFailed] = React.useState(false);
  const file = (onDark && integ.fileDark) || integ.file || `${integ.slug}.svg`;
  const src = `/logos/integrations/${file}`;
  const contextClass = `sw-int-logo--${integ.slug}${onDark ? ' sw-int-logo--on-dark' : ''}`;

  if (inline) {
    return (
      <span
        className={`sw-int-logo sw-int-logo--inline ${contextClass} ${className}`.trim()}
        style={{ width: size, height: size }}
        aria-hidden="true"
      >
        {React.cloneElement(inline, { width: size, height: size })}
      </span>
    );
  }

  if (!imgFailed) {
    return (
      <span
        className={`sw-int-logo ${imgLoaded ? 'sw-int-logo--asset' : 'sw-int-logo--pending'} ${contextClass} ${className}`.trim()}
        style={{ width: size, height: size, '--sw-int-color': integ.color }}
        aria-hidden="true"
      >
        <img
          src={src}
          alt=""
          width={size}
          height={size}
          loading="lazy"
          onLoad={() => setImgLoaded(true)}
          onError={() => setImgFailed(true)}
          className="sw-int-logo-img"
        />
      </span>
    );
  }

  const initials = name
    .replace(/[^A-Za-z0-9 ]/g, '')
    .split(/\s+/)
    .map(w => w[0])
    .join('')
    .slice(0, 2)
    .toUpperCase();
  return (
    <span
      className={`sw-int-logo sw-int-logo--monogram ${className}`.trim()}
      style={{ width: size, height: size, background: integ.color }}
      aria-hidden="true"
    >
      <span className="sw-int-logo-monogram">{initials}</span>
    </span>
  );
}

